import { FC } from 'react';
import { Box, Checkbox, CircularProgress, FormControlLabel, Typography } from '@mui/material';
import { TPaymentServices, TServicesSelection } from 'services/api/paymentMethods/types';

type TServicesSelectorProps = {
	services: TPaymentServices | null;
	value: TServicesSelection;
	onChange: (value: TServicesSelection) => void;
	disabled?: boolean;
};

/** "All services" switch plus a checkbox per active service of the payment gateway. */
export const ServicesSelector: FC<TServicesSelectorProps> = ({
	services,
	value,
	onChange,
	disabled = false,
}) => {
	const toggleService = (id: number, checked: boolean) => {
		const serviceIds = checked
			? Array.from(new Set([...value.serviceIds, id]))
			: value.serviceIds.filter((serviceId) => serviceId !== id);
		onChange({ allServices: false, serviceIds });
	};

	return (
		<Box>
			<FormControlLabel
				control={
					<Checkbox
						checked={value.allServices}
						disabled={disabled}
						onChange={(event) =>
							onChange({ allServices: event.target.checked, serviceIds: value.serviceIds })
						}
					/>
				}
				label={<Typography sx={{ fontWeight: 600 }}>All services</Typography>}
			/>
			{!services && <CircularProgress size={18} sx={{ ml: 2 }} />}
			<Box sx={{ display: 'flex', flexDirection: 'column', pl: 3 }}>
				{services?.map((service) => (
					<FormControlLabel
						key={service.id}
						control={
							<Checkbox
								size="small"
								checked={value.allServices || value.serviceIds.includes(service.id)}
								disabled={disabled || value.allServices}
								onChange={(event) => toggleService(service.id, event.target.checked)}
							/>
						}
						label={
							<Box>
								<Typography sx={{ fontSize: '.9rem' }}>{service.name}</Typography>
								{service.description && (
									<Typography sx={{ fontSize: '.75rem', color: 'text.secondary' }}>
										{service.description}
									</Typography>
								)}
							</Box>
						}
					/>
				))}
			</Box>
		</Box>
	);
};

export const isServicesSelectionValid = (value: TServicesSelection) =>
	value.allServices || value.serviceIds.length > 0;
